/// <reference types="fhir" />

import { Injectable, Inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Actions } from "@ngrx/effects";
import { AppState } from "../models/app.state";
import { ENV_VARS } from "../environment-variables/environment-variables.module";
import "rxjs/add/operator/map";
import "rxjs/add/operator/filter";
import { HttpClient, HttpParams } from "@angular/common/http";
import * as FhirActions from "../store/actions/fhir.actions";
import * as jsonpath from "jsonpath";
import { FlattenedFhirState, UserTypes, IDiaprofilUser } from "../models/types";
import { getFlattenedFhir } from "../store/selectors/fhir.selectors";
import { of } from "rxjs/observable/of";
import {
  concatMap,
  scan,
  distinctUntilChanged,
  switchMap
} from "rxjs/operators";
import { Observable } from "rxjs/Observable";
import { Subscription } from "rxjs/Subscription";
import { Subject } from "rxjs/Subject";
import { forkJoin } from "rxjs/observable/forkJoin";
import { brokenUserUuidMapping } from "../utils/broken-user-uuid-mapping";

export type httpMethodTypes = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

@Injectable()
export class FhirService {
  // TODO: Right now presumes that we are working with example data, will need major refactoring
  baseUrl: URL;
  subTasks: FlattenedFhirState;
  endpoints: { [key: string]: (method: httpMethodTypes) => string };
  fhir: fhir.Resource[] = [];
  processedResourcesSubject: Subject<string>;
  processedResourcesObservable: Observable<string[]>;
  processedResourcesSubscription: Subscription;
  processedResources: Set<string> = new Set([]);
  existingResources: string[] = [];
  userType: UserTypes = "Practitioner";
  constructor(
    public http: HttpClient,
    public store: Store<AppState>,
    public actions$: Actions,
    @Inject(ENV_VARS) public envVars
  ) {
    this.startProcessedResourcesTracking();
    this.store
      .select(state => state.fhir)
      .subscribe(fhir => (this.fhir = fhir));
    this.store.select(getFlattenedFhir).subscribe(flattenedFhir => {
      this.subTasks = flattenedFhir;
    });
    this.baseUrl = envVars.API_URL;
    this.endpoints = {
      fhir: (method): string => {
        return "@fhir";
      },
      addTasks: (): string => {
        return "@add_tasks";
      },
      fhirUser: (): string => {
        return "@fhir-user";
      }
    };
    this.store
      .select(state => state.fhir)
      .subscribe(fhir => {
        this.existingResources = fhir.reduce((result, resource) => {
          result = [...result, resource.id];
          return result;
        }, []);
      });
  }

  startProcessedResourcesTracking() {
    /* Function to initialize / reset tracking of loaded resources */
    this.processedResourcesSubject = new Subject();
    this.processedResourcesObservable = this.processedResourcesSubject.pipe(
      scan((existingList: string[], nextValue: string) => {
        if (existingList.indexOf(nextValue) === -1) {
          return [...existingList, nextValue];
        } else {
          return existingList;
        }
      }, [])
    );
    this.processedResourcesSubscription = this.processedResourcesObservable
      .pipe(distinctUntilChanged())
      .subscribe(result => (this.processedResources = new Set(result)));
  }

  resolveReference(reference: string) {
    // TODO: Resolve other reference types than relative references
    const split = reference.split("/"),
      resourceType = split[0],
      resourceId = split[1];
    const resource = this.fhir.find(
      resource =>
        resource.resourceType === resourceType && resource.id === resourceId
    );
    if (resource === undefined && resourceType !== "Organization") {
      //TODO: Remove the temporary hardcoding of Organization not being resolved
      return this.getResource(resourceType, resourceId);
    } else {
      return resource;
    }
  }

  addTasks(params: {
    owner_reference?: string;
    patient_reference?: string;
    requester_reference: string;
    focus_reference?: string;
    task_type: string;
    task_end_date: string;
    return_only_parent_task: boolean;
    extensions?: fhir.Extension[];
  }): Observable<fhir.Resource[]> {
    return this.http.post<fhir.Resource[]>(
      `${this.baseUrl}${this.endpoints.addTasks("POST")}`,
      params
    );
  }

  addResourcesToState(args: {
    resources: fhir.Resource[];
    overwrite?: boolean;
    resolveRefs?: boolean;
  }) {
    args.overwrite = args.overwrite !== undefined ? args.overwrite : false;
    args.resolveRefs = args.resolveRefs !== undefined ? args.resolveRefs : true;
    const newResourceIds = new Set(args.resources.map(x => x.id));
    for (const resource of args.resources) {
      this.processedResourcesSubject.next(resource.id);
      const references = Array.from(
        new Set(
          jsonpath.query(resource, "$..reference").filter(reference => {
            const resourceId = reference.split("/")[1];
            return (
              !this.processedResources.has(resourceId) &&
              !newResourceIds.has(resourceId)
            );
          })
        )
      ) as string[];
      // TODO: Right now we can only handle relative references
      if (args.resolveRefs === true) {
        for (const reference of references) {
          if (!reference.includes("/")) {
            throw Error(
              `There is a broken relative reference '${reference}'in the FHIR resource '${resource.id}'`
            );
          } else {
            this.processedResourcesSubject.next(reference);
            const [refResourceType, refResourceId] = reference.split("/");
            if (refResourceType !== "Organization") {
              //TODO: Remove the temporary hardcoding of Organization not being resolved
              this.getResource(refResourceType, refResourceId);
            }
          }
        }
      }
      if (args.overwrite === true) {
        this.store.dispatch(new FhirActions.ReplaceAction(resource));
      } else {
        this.store.dispatch(new FhirActions.AddAction(resource));
      }
    }
  }

  getResource(
    resourceType,
    resourceId,
    overwrite = false,
    extraAction: (resource: fhir.Resource) => void = () => {}
  ) {
    return this.http
      .get<fhir.Resource>(
        `${this.baseUrl}${this.endpoints.fhir(
          "GET"
        )}/${resourceType}/${resourceId}`
      )
      .subscribe(
        resource => {
          this.addResourcesToState({ resources: [resource], overwrite });
          extraAction(resource);
        },
        // Errors will call this callback instead:
        err => {
          console.log(err);
        }
      );
  }

  createUser(params: {}) {
    return this.http.post<IDiaprofilUser>(
      `${this.baseUrl}${this.endpoints.fhirUser("POST")}`,
      params
    );
  }

  patchUser(userName: string, patch: { [key: string]: any }) {
    const userUuidMapping = brokenUserUuidMapping;
    userName = userUuidMapping[userName] || userName;
    return this.http.patch(
      `${this.baseUrl}${this.endpoints.fhirUser("PATCH")}/${userName}`,
      patch
    );
  }

  getQuery(params: {
    resourceType: string;
    queryParams?: { [key: string]: any };
    queryString?: string;
  }) {
    let options = { params: {} };
    if (
      params.hasOwnProperty("queryString") &&
      params.hasOwnProperty("queryParams")
    ) {
      const fromObj = new HttpParams({ fromObject: params.queryParams });
      const fromString = new HttpParams({ fromString: params.queryString });
      const combinedString = `${fromObj.toString()}&${fromString.toString()}`;
      options = {
        params: new HttpParams({ fromString: combinedString })
      };
    } else {
      if (!!params.queryParams) {
        options = {
          params: new HttpParams({ fromObject: params.queryParams })
        };
      }
      if (!!params.queryString) {
        options = {
          params: new HttpParams({ fromString: params.queryString })
        };
      }
    }
    return this.http.get<fhir.Bundle>(
      `${this.baseUrl}${this.endpoints.fhir("GET")}/${params.resourceType}`,
      options
    );
  }

  patchResource(params: {
    resourceType: "Task" | "ProcedureRequest" | "QuestionnaireResponse";
    resourceId: string;
    operations: any;
  }) {
    return this.http.patch(
      `${this.baseUrl}${this.endpoints.fhir("PATCH")}/${params.resourceType}/${
        params.resourceId
      }`,
      { patch: params.operations }
    );
  }

  deleteResource(params: {
    resourceType: "Task" | "ProcedureRequest" | "QuestionnaireResponse";
    resourceId: string;
  }) {
    const queryString = `${this.baseUrl}${this.endpoints.fhir("DELETE")}/${
      params.resourceType
    }/${params.resourceId}`;
    return this.http.delete(queryString);
  }

  deleteQuery(
    resourceType: "Task" | "ProcedureRequest" | "QuestionnaireResponse" = null,
    queryParams: { [key: string]: any }
  ) {
    let endpoint = `${this.baseUrl}${this.endpoints.fhir("DELETE")}`;
    if (resourceType !== null) {
      endpoint = `${endpoint}/${resourceType}`;
    }
    const options = {
      params: new HttpParams({ fromObject: queryParams })
    };
    return this.http.delete(endpoint, options);
  }

  deleteParentTaskAndAssociatedResources(parentTask: fhir.Task) {
    const groupIdentifier = parentTask.groupIdentifier.value;
    const queries = [
      this.deleteResource({ resourceType: "Task", resourceId: parentTask.id }),
      this.deleteQuery("Task", {
        code: "|subTask",
        "group-identifier": groupIdentifier
      }),
      this.deleteQuery("ProcedureRequest", {
        requisition: groupIdentifier
      })
    ];
    return forkJoin(queries).pipe(
      switchMap(result => {
        return of({});
      })
    );
  }

  postQuestionnaireResponse(params: {
    responseResource: fhir.QuestionnaireResponse;
    procedureRequestId?: string; // Provide if you want to change associated ProcedureRequest.status to 'complete'
    subTaskId?: string; // Provide if you want to change associated Task.status to 'complete'
  }) {
    const requests: {
      requestType: "post" | "patch";
      resourceType: "QuestionnaireResponse" | "ProcedureRequest" | "Task";
      id?: string;
      body:
        | fhir.QuestionnaireResponse
        | { patch: [{ op: string; path: string; value: string }] };
    }[] = [
      {
        requestType: "post",
        resourceType: "QuestionnaireResponse",
        body: params.responseResource
      }
    ];
    if (params.procedureRequestId !== undefined) {
      requests.push({
        requestType: "patch",
        resourceType: "ProcedureRequest",
        id: params.procedureRequestId,
        body: {
          patch: [{ op: "replace", path: "/status", value: "completed" }]
        }
      });
    }
    if (params.subTaskId !== undefined) {
      requests.push({
        requestType: "patch",
        resourceType: "Task",
        id: params.subTaskId,
        body: {
          patch: [{ op: "replace", path: "/status", value: "completed" }]
        }
      });
    }
    return of(...requests).pipe(
      concatMap(curRequest => {
        if (curRequest.requestType === "post") {
          return this.http
            .post(
              `${this.baseUrl}${this.endpoints.fhir("POST")}/${
                curRequest.resourceType
              }`,
              curRequest.body
            )
            .map(result => {
              this.store.dispatch(new FhirActions.AddAction(result));
              return of(result);
            });
        } else {
          return this.http
            .patch(
              `${this.baseUrl}${this.endpoints.fhir("PATCH")}/${
                curRequest.resourceType
              }/${curRequest.id}`,
              curRequest.body
            )
            .map(result => {
              this.getResource(curRequest.resourceType, curRequest.id, true);
              return of(result);
            });
        }
      })
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/fhir.service.ts